// src/pages/legal/PrivacyPage.js
import React from 'react';
import { useSEO } from '../../lib/useSEO';

const S = {
  page:  { maxWidth: 760, margin: '0 auto', padding: '48px 24px 80px' },
  h1:    { fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(2rem,5vw,2.8rem)', letterSpacing:'.04em', marginBottom:6, color:'#f4f4f4' },
  meta:  { fontSize:'.78rem', color:'#444', marginBottom:40 },
  h2:    { fontFamily:"'Barlow Condensed',sans-serif", fontSize:'1.05rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'#4cca74', margin:'36px 0 10px', paddingBottom:6, borderBottom:'1px solid rgba(76,202,116,.15)' },
  p:     { color:'#888', fontSize:'.9rem', lineHeight:1.85, marginBottom:14 },
  li:    { color:'#888', fontSize:'.9rem', lineHeight:1.85, marginBottom:6 },
  ul:    { paddingLeft:20, marginBottom:14 },
  b:     { color:'#ccc', fontWeight:600 },
  box:   { background:'rgba(76,202,116,.06)', border:'1px solid rgba(76,202,116,.2)', borderRadius:10, padding:'14px 18px', marginBottom:20 },
  th:    { background:'#111', color:'#4cca74', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', padding:'10px 14px', textAlign:'left', border:'1px solid rgba(255,255,255,.07)' },
  td:    { color:'#888', padding:'10px 14px', border:'1px solid rgba(255,255,255,.07)', verticalAlign:'top', lineHeight:1.6, fontSize:'.82rem' },
};
const Table = ({ rows, heads }) => (
  <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:20, fontSize:'.82rem' }}>
    <thead><tr>{heads.map((h,i)=><th key={i} style={S.th}>{h}</th>)}</tr></thead>
    <tbody>{rows.map((r,i)=><tr key={i} style={{background:i%2===0?'rgba(255,255,255,.02)':'none'}}>{r.map((c,j)=><td key={j} style={j===0?{...S.td,color:'#ccc',fontWeight:600}:S.td}>{c}</td>)}</tr>)}</tbody>
  </table>
);

export default function PrivacyPage() {
  useSEO({ title:'Privacy Policy', canonical:'/legal/privacy', noIndex:true });
  return (
    <div style={S.page}>
      <p style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.72rem', fontWeight:700, letterSpacing:'.2em', textTransform:'uppercase', color:'#4cca74', marginBottom:8 }}>Legal</p>
      <h1 style={S.h1}>Privacy Policy</h1>
      <p style={S.meta}>CosmiqHerbz s.r.o. · Last updated: June 2025 · GDPR Regulation (EU) 2016/679</p>
      <p style={S.p}>This Privacy Policy explains how CosmiqHerbz s.r.o. ("we", "us") collects, uses, and protects personal data when you use cosmiqherbz.com ("Website").</p>

      <h2 style={S.h2}>1. Data Controller</h2>
      <div style={S.box}>
        <p style={{...S.p,marginBottom:2}}><b style={S.b}>CosmiqHerbz s.r.o.</b></p>
        <p style={{...S.p,marginBottom:2}}>30. dubna 2b, Ostrava, Moravskoslezský kraj, Czech Republic</p>
        <p style={{...S.p,marginBottom:0}}>Email: <a href="mailto:info@cosmiqherbz.com" style={{color:'#4cca74',textDecoration:'none'}}>info@cosmiqherbz.com</a></p>
      </div>

      <h2 style={S.h2}>2. Data We Collect</h2>
      <Table heads={['Category','Data Collected','Source']} rows={[
        ['Account data','Name, email address, encrypted password','You provide on registration'],
        ['Order data','Name, email, delivery address, phone, items, order value, payment reference','You provide at checkout'],
        ['Age verification','Gate result, session ID, country, timestamp','Auto-collected on site entry'],
        ['Technical data','IP address, browser, device, pages visited','Auto-collected via server logs'],
        ['Cookie data','Consent status, session preferences, cart','Stored locally / on our servers'],
        ['Marketing data','Newsletter subscription and engagement data','With your explicit consent only'],
      ]} />

      <h2 style={S.h2}>3. Legal Basis &amp; Purposes</h2>
      <Table heads={['Purpose','Legal Basis (GDPR Art. 6)']} rows={[
        ['Processing and fulfilling orders','Art. 6(1)(b) — contract performance'],
        ['Order confirmations and shipping notifications','Art. 6(1)(b) — contract performance'],
        ['Age verification and site access control','Art. 6(1)(c) — legal obligation'],
        ['Fraud prevention and platform security','Art. 6(1)(f) — legitimate interests'],
        ['Tax and accounting compliance','Art. 6(1)(c) — legal obligation'],
        ['Customer support and complaints','Art. 6(1)(b) — contract performance'],
        ['Newsletter and marketing emails','Art. 6(1)(a) — explicit consent'],
        ['Website analytics','Art. 6(1)(f) — legitimate interests (with cookie consent)'],
        ['Loyalty programme','Art. 6(1)(b) — contract performance'],
      ]} />

      <h2 style={S.h2}>4. Data Sharing</h2>
      <p style={S.p}>We do not sell personal data. We share data only where necessary with:</p>
      <ul style={S.ul}>
        <li style={S.li}><b style={S.b}>Payment processors</b> — to process transactions securely under their own privacy policies.</li>
        <li style={S.li}><b style={S.b}>Shipping carriers</b> — name and address to fulfil delivery.</li>
        <li style={S.li}><b style={S.b}>Email service providers</b> — for transactional and marketing emails.</li>
        <li style={S.li}><b style={S.b}>Cloud hosting</b> — Supabase (database) and Netlify (website hosting).</li>
        <li style={S.li}><b style={S.b}>Legal authorities</b> — where required by law or court order.</li>
      </ul>
      <p style={S.p}>Where data is transferred outside the EEA, appropriate safeguards (Standard Contractual Clauses) are in place.</p>

      <h2 style={S.h2}>5. Retention Periods</h2>
      <Table heads={['Data','Retention']} rows={[
        ['Order and invoice records','10 years (Czech accounting law)'],
        ['Account data','Active account life + 3 years post-last activity'],
        ['Age verification logs','3 years'],
        ['Marketing consent records','Consent period + 3 years'],
        ['Support correspondence','3 years from resolution'],
        ['Analytics / server logs','26 months'],
        ['Cookie consent records','13 months'],
      ]} />

      <h2 style={S.h2}>6. Your Rights</h2>
      <p style={S.p}>Under GDPR you have the right to: <b style={S.b}>access</b> your data · <b style={S.b}>rectify</b> inaccuracies · <b style={S.b}>erasure</b> where no legal retention applies · <b style={S.b}>restrict</b> processing · <b style={S.b}>data portability</b> · <b style={S.b}>object</b> to processing · <b style={S.b}>withdraw consent</b> at any time.</p>
      <p style={S.p}>Send requests to <a href="mailto:info@cosmiqherbz.com" style={{color:'#4cca74',textDecoration:'none'}}>info@cosmiqherbz.com</a>. We respond within 30 days. You may also complain to the Czech supervisory authority:</p>
      <div style={S.box}>
        <p style={{...S.p,marginBottom:2}}><b style={S.b}>Úřad pro ochranu osobních údajů (ÚOOÚ)</b></p>
        <p style={{...S.p,marginBottom:0}}>Pplk. Sochora 27, 170 00 Praha 7 · <a href="https://www.uoou.cz" style={{color:'#4cca74',textDecoration:'none'}} target="_blank" rel="noopener noreferrer">www.uoou.cz</a></p>
      </div>

      <h2 style={S.h2}>7. Cookies</h2>
      <p style={S.p}>We use essential and optional cookies. Non-essential cookies require your explicit consent. See our full <a href="/legal/cookies" style={{color:'#4cca74',textDecoration:'none'}}>Cookie Policy</a> for details and opt-out instructions.</p>

      <h2 style={S.h2}>8. Security</h2>
      <p style={S.p}>We apply appropriate technical and organisational security measures. Passwords are stored in hashed form. Payment data is processed exclusively by our payment provider and never stored on our systems.</p>

      <h2 style={S.h2}>9. Minors</h2>
      <p style={S.p}>Our Website is intended for persons aged 18 and over. We do not knowingly collect data from minors. If we become aware of such data, we will delete it promptly.</p>

      <h2 style={S.h2}>10. Updates</h2>
      <p style={S.p}>We may update this policy. The current version is always available on the Website. Material changes will be communicated to registered users by email.</p>
    </div>
  );
}